import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  Package,
  DollarSign,
  Calendar,
  FileText,
  Search,
  ChevronDown,
  X,
  Upload
} from 'lucide-react';
import { purchasesAPI, itemsAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import { usePurchasePdf } from '../../hooks/usePurchasePdf';
import dayjs from '../../utils/dayjs';
import ImportCsvModal from './ImportCsvModal';
import TotalsBar from '../ui/TotalsBar';

const PurchaseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();
  const { downloadForPurchase } = usePurchasePdf();
  const isEditing = Boolean(id);

  // Form setup
  const { register, control, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm({
    shouldUnregister: false,
    defaultValues: {
      PurchaseDate: dayjs().format('YYYY-MM-DD'),
      PurchaseRemarks: '',
      Remarks: '',
      details: []
    }
  });

  const { fields, append, prepend, remove, move } = useFieldArray({
    control,
    name: 'details'
  });

  const rowsContainerRef = useRef(null);
  // The rows list scrolls with the whole page (window), not a cramped inner
  // scrollbox — so an open item dropdown is never clipped and has full room.
  // scrollMargin is the list's offset from the top of the document; content
  // above it (barcode results, queue progress, errors) can change height, so
  // we re-measure it below.
  const [listScrollMargin, setListScrollMargin] = useState(0);
  const rowVirtualizer = useWindowVirtualizer({
    count: fields.length,
    estimateSize: () => 220,
    overscan: 8,
    scrollMargin: listScrollMargin,
  });

  const measureListOffset = useCallback(() => {
    const el = rowsContainerRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY;
    // Only update when it actually moved, so the every-render effect settles.
    setListScrollMargin((prev) => (Math.abs(prev - top) > 1 ? top : prev));
  }, []);

  // Re-measure after every render (content above the list can grow/shrink)…
  useLayoutEffect(() => {
    measureListOffset();
  });

  // …and on window resize, which may not trigger a React render.
  useEffect(() => {
    window.addEventListener('resize', measureListOffset);
    return () => window.removeEventListener('resize', measureListOffset);
  }, [measureListOffset]);

  // State for items and purchase data
  const [items, setItems] = useState([]);
  const [purchaseData, setPurchaseData] = useState(null);
  const [isLoadingPurchase, setIsLoadingPurchase] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // CSV import
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Which submit button was clicked. A draft records the purchase without
  // moving stock; going live applies stock immediately. CSV-imported rows land
  // in this same form, so "Save as Draft" covers imports too.
  const submitModeRef = useRef('live');

  // Barcode scanning state
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [isSearchingByBarcode, setIsSearchingByBarcode] = useState(false);
  const [barcodeQueue, setBarcodeQueue] = useState([]);
  const [isProcessingBarcodeQueue, setIsProcessingBarcodeQueue] = useState(false);
  const barcodeInputRef = useRef(null);
  const quantityInputRefs = useRef({});
  // Track previous processing state to run a callback after processing completes
  const wasProcessingBarcodeRef = useRef(false);

  // Barcode/search input dropdown state
  const [barcodeSearchResults, setBarcodeSearchResults] = useState([]);
  const [isBarcodeSearching, setIsBarcodeSearching] = useState(false);
  const [showBarcodeDropdown, setShowBarcodeDropdown] = useState(false);
  // Which result Enter will add. A name search lists a product and all its
  // sizes, so "always the first" picks the wrong row; ↑/↓ move this.
  const [barcodeActiveIndex, setBarcodeActiveIndex] = useState(0);
  const barcodeActiveRowRef = useRef(null);

  // Searchable dropdown state
  const [dropdownStates, setDropdownStates] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [isSearching, setIsSearching] = useState({});
  const [selectedItems, setSelectedItems] = useState({});

  // Fetch items for dropdown
  const fetchItems = async () => {
    try {
      const response = await itemsAPI.getAll({ limit: 1000, include_out_of_stock: true });
      setItems(response.data || []);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load items'
      });
    }
  };

  // Fetch purchase data if editing
  const fetchPurchase = async () => {
    if (!isEditing) return;
    
    try {
      setIsLoadingPurchase(true);
      const response = await purchasesAPI.get(id);
      setPurchaseData(response.data);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load purchase data'
      });
    } finally {
      setIsLoadingPurchase(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchItems();
    if (isEditing) {
      fetchPurchase();
    }
  }, [isEditing, id]);

  // Focus barcode input on component mount
  useEffect(() => {
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  // Callback-style focus after barcode processing toggles from true -> false
  useEffect(() => {
    if (wasProcessingBarcodeRef.current && !isProcessingBarcodeQueue) {
      // Processing just finished; ensure barcode gets focus now that UI settled
      focusBarcodeInput();
    }
    wasProcessingBarcodeRef.current = isProcessingBarcodeQueue;
  }, [isProcessingBarcodeQueue]);

  // API-based search function
  const searchItems = useCallback(async (index, searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults(prev => ({
        ...prev,
        [index]: []
      }));
      return;
    }

    setIsSearching(prev => ({
      ...prev,
      [index]: true
    }));

    try {
      const response = await itemsAPI.search(searchTerm, { include_out_of_stock: true });
      setSearchResults(prev => ({
        ...prev,
        [index]: response.data || []
      }));
    } catch (error) {
      console.error('Error searching items:', error);
      setSearchResults(prev => ({
        ...prev,
        [index]: []
      }));
    } finally {
      setIsSearching(prev => ({
        ...prev,
        [index]: false
      }));
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setDropdownStates({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounced search effect for each dropdown
  useEffect(() => {
    const timeouts = {};
    
    Object.keys(searchTerms).forEach(index => {
      const searchTerm = searchTerms[index];
      if (searchTerm !== undefined) {
        // Clear existing timeout
        if (timeouts[index]) {
          clearTimeout(timeouts[index]);
        }
        
        // Set new timeout for debounced search
        timeouts[index] = setTimeout(() => {
          searchItems(parseInt(index), searchTerm);
        }, 300);
      }
    });

    // Cleanup function
    return () => {
      Object.values(timeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [searchTerms, searchItems]);

  // Set form data when purchase is loaded
  useEffect(() => {
    if (purchaseData && isEditing) {
      setValue('PurchaseDate', purchaseData.PurchaseDate ? dayjs(purchaseData.PurchaseDate).format('YYYY-MM-DD') : '');
      setValue('PurchaseRemarks', purchaseData.PurchaseRemarks || '');
      setValue('Remarks', purchaseData.Remarks || '');
      const details = purchaseData.details || [];
      setValue('details', details);
      // Seed the item cache from each detail's eager-loaded item. The Item field
      // resolves its label via selectedItems first, so without this a purchased
      // item outside the capped preloaded list would render "Select an item".
      const loadedItems = {};
      details.forEach((detail) => {
        if (detail.item && detail.SaleItem_ID != null) {
          loadedItems[detail.SaleItem_ID] = detail.item;
        }
      });
      if (Object.keys(loadedItems).length > 0) {
        setSelectedItems((prev) => ({ ...prev, ...loadedItems }));
      }
    }
  }, [purchaseData, isEditing, setValue]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      
      // Convert date to UTC ISO string
      const formattedData = {
        ...data,
        Status: submitModeRef.current,
        PurchaseDate: dayjs(data.PurchaseDate).utc().toISOString(),
        details: data.details.map(detail => ({
          ...detail,
          Quantity: parseFloat(detail.Quantity) || 0,
          Price: parseFloat(detail.Price) || 0,
          SaleItem_ID: detail.SaleItem_ID ? parseInt(detail.SaleItem_ID) : null,
        }))
      };

      if (isEditing) {
        await purchasesAPI.update(id, formattedData);
        addNotification({
          type: 'success',
          title: 'Success',
          message: 'Purchase updated successfully'
        });
      } else {
        const response = await purchasesAPI.create(formattedData);
        addNotification({
          type: 'success',
          title: 'Success',
          message: 'Purchase created successfully'
        });

        // Only a live purchase gets a PDF. A draft has not touched stock yet,
        // so there is nothing to check a delivery against — it gets its
        // document when it is taken live instead.
        if (submitModeRef.current === 'live') {
          await downloadForPurchase(response.data?.ID);
        }
      }

      navigate('/purchases');
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} purchase`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addDetail = () => {
    prepend({ Quantity: '', Price: '', SaleItem_ID: null, Remarks: '' });
    // Open the item dropdown for the newly added top row and focus its search input
    setDropdownStates(prev => ({
      ...prev,
      0: true
    }));
    // Defer focusing to after the dropdown renders
    setTimeout(() => {
      const searchInput = document.querySelector('.dropdown-container input[placeholder="Search items..."]');
      if (searchInput) {
        searchInput.focus();
        searchInput.select();
      }
    }, 0);
  };

  // Merge preview rows into the form. Matches the barcode behaviour: an item
  // already in the purchase gets its quantity increased rather than a second row.
  const handleImportConfirm = ({ rows, importedRowCount, failedRowCount, skippedRowCount }) => {
    const current = getValues('details') || [];
    const next = [...current];
    const indexByItemId = new Map();
    next.forEach((detail, index) => {
      if (detail?.SaleItem_ID) indexByItemId.set(detail.SaleItem_ID, index);
    });

    const importedItems = {};

    rows.forEach((row) => {
      const item = row.item;
      if (!item) return;
      importedItems[item.ID] = item;

      const quantity = parseFloat(row.quantity) || 0;
      const existingIndex = indexByItemId.get(item.ID);

      if (existingIndex !== undefined) {
        const previous = parseFloat(next[existingIndex].Quantity) || 0;
        next[existingIndex] = {
          ...next[existingIndex],
          Quantity: String(previous + quantity),
        };
      } else {
        indexByItemId.set(item.ID, next.length);
        next.push({
          Quantity: String(quantity),
          Price: item.Price || 0,
          SaleItem_ID: item.ID,
          Remarks: '',
        });
      }
    });

    // One write, not one per row: avoids re-rendering the list N times and
    // avoids prepend() reversing the file's order.
    setValue('details', next, { shouldDirty: true });

    // Keep the imported items available to the row dropdowns.
    setSelectedItems((prev) => ({ ...prev, ...importedItems }));
    setItems((prev) => {
      const previous = Array.isArray(prev) ? prev : [];
      const known = new Set(previous.map((i) => i?.ID));
      const additions = Object.values(importedItems).filter((i) => !known.has(i.ID));
      return additions.length ? [...additions, ...previous] : previous;
    });

    setImportResult({
      imported: importedRowCount,
      failed: failedRowCount,
      skipped: skippedRowCount,
    });
    setIsImportOpen(false);

    addNotification({
      type: failedRowCount > 0 ? 'warning' : 'success',
      title: 'CSV imported',
      message: `${importedRowCount} row${importedRowCount === 1 ? '' : 's'} imported`
        + (failedRowCount > 0 ? `, ${failedRowCount} failed` : ''),
    });
  };

  const removeDetail = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  // Searchable dropdown helper functions
  const toggleDropdown = (index) => {
    setDropdownStates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const setSearchTerm = (index, term) => {
    setSearchTerms(prev => ({
      ...prev,
      [index]: term
    }));
  };

  const selectItem = (index, item) => {
    setValue(`details.${index}.SaleItem_ID`, item.ID);
    setValue(`details.${index}.Price`, item.Price || 0);
    setValue(`details.${index}.Quantity`, '1');
    setSelectedItems(prev => ({
      ...prev,
      [item.ID]: item
    }));
    setDropdownStates(prev => ({
      ...prev,
      [index]: false
    }));
    setSearchTerms(prev => ({
      ...prev,
      [index]: ''
    }));
    setSearchResults(prev => ({
      ...prev,
      [index]: []
    }));
  };

  const getFilteredItems = (index) => {
    const searchTerm = searchTerms[index] || '';
    if (!searchTerm.trim()) {
      return items; // Show all items when no search term
    }
    return searchResults[index] || [];
  };

  const getSelectedItem = (index) => {
    const selectedId = watch(`details.${index}.SaleItem_ID`);
    // Check cached selection first, then search results, then preloaded items
    if (selectedItems[selectedId]) return selectedItems[selectedId];
    const searchResultsForIndex = searchResults[index] || [];
    const foundInSearch = searchResultsForIndex.find(item => item.ID === selectedId);
    if (foundInSearch) return foundInSearch;

    return items.find(item => item.ID === selectedId);
  };

  // Search-as-you-type for barcode input
  useEffect(() => {
    const trimmed = barcodeSearch.trim();
    if (!trimmed || trimmed.length < 2) {
      setBarcodeSearchResults([]);
      setBarcodeActiveIndex(0);
      setShowBarcodeDropdown(false);
      return;
    }

    // Set once the text has moved on, so a slow response for an older query
    // can't replace newer results or reopen the list after the box was cleared.
    let cancelled = false;
    const debounce = setTimeout(async () => {
      setIsBarcodeSearching(true);
      try {
        const response = await itemsAPI.search(trimmed, { include_out_of_stock: true });
        if (cancelled) return;
        setBarcodeSearchResults(response.data || []);
        setBarcodeActiveIndex(0);
        setShowBarcodeDropdown(true);
      } catch (error) {
        console.error('Error searching items:', error);
        if (!cancelled) setBarcodeSearchResults([]);
      } finally {
        if (!cancelled) setIsBarcodeSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
      setIsBarcodeSearching(false);
    };
  }, [barcodeSearch]);

  // Keep the highlighted result visible while arrowing through a long list
  useEffect(() => {
    barcodeActiveRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [barcodeActiveIndex]);

  // Add item from barcode/search input
  const addItemFromSearch = useCallback((item) => {
    // Ensure item exists in preloaded items list
    setItems(prev => {
      const previous = Array.isArray(prev) ? prev : [];
      if (previous.some(i => i && i.ID === item.ID)) return previous;
      return [item, ...previous];
    });
    // If item already exists in details, increment quantity
    const currentDetails = getValues('details') || [];
    const existingIndex = currentDetails.findIndex(d => d && d.SaleItem_ID === item.ID);
    if (existingIndex !== -1) {
      const currentQuantity = parseFloat(currentDetails[existingIndex].Quantity) || 0;
      setValue(`details.${existingIndex}.Quantity`, String(currentQuantity + 1));
      try {
        if (existingIndex > 0) move(existingIndex, 0);
      } catch (e) { /* ignore */ }
    } else {
      prepend({ Quantity: '1', Price: item.Price || 0, SaleItem_ID: item.ID, Remarks: '' });
      setSelectedItems(prev => ({ ...prev, [item.ID]: item }));
    }
    setBarcodeSearch('');
    setBarcodeSearchResults([]);
    setShowBarcodeDropdown(false);
    focusBarcodeInput();
  }, [getValues, setValue, move, prepend, setSelectedItems]);

  // Barcode scanning functions
  const handleBarcodeSearch = useCallback(async () => {
    const trimmed = String(barcodeSearch || '').trim();
    if (!trimmed) return;

    // If search results exist, add the highlighted one (the first by default)
    if (barcodeSearchResults.length > 0) {
      addItemFromSearch(barcodeSearchResults[barcodeActiveIndex] || barcodeSearchResults[0]);
      return;
    }

    // Fall back to exact barcode lookup
    setBarcodeQueue(prev => [...prev, trimmed]);
    setBarcodeSearch('');
    setShowBarcodeDropdown(false);
  }, [barcodeSearch, barcodeSearchResults, barcodeActiveIndex, addItemFromSearch]);

  // Process the barcode queue sequentially
  useEffect(() => {
    const processNext = async () => {
      if (isProcessingBarcodeQueue || barcodeQueue.length === 0) return;
      setIsProcessingBarcodeQueue(true);
      setIsSearchingByBarcode(true);
      const nextBarcode = barcodeQueue[0];
      try {
        const response = await itemsAPI.getById(nextBarcode, { include_out_of_stock: true });
        if (response && response.data && response.data.length > 0) {
          const item = response.data[0];
          // Ensure scanned item exists in preloaded items list for dropdown/search
          setItems(prev => {
            const previous = Array.isArray(prev) ? prev : [];
            if (previous.some(i => i && i.ID === item.ID)) return previous;
            return [item, ...previous];
          });
          // If item already exists in details, increment its quantity; otherwise add new row
          const currentDetails = getValues('details') || [];
          const existingIndex = currentDetails.findIndex(d => d && d.SaleItem_ID === item.ID);
          if (existingIndex !== -1) {
            const currentQuantity = parseFloat(currentDetails[existingIndex].Quantity) || 0;
            setValue(`details.${existingIndex}.Quantity`, String(currentQuantity + 1));
            // Reorder: bring the updated item to the top
            try {
              if (existingIndex > 0) {
                move(existingIndex, 0);
              }
            } catch (e) {
              // ignore reordering errors
            }
          } else {
            prepend({
              Quantity: '1',
              Price: item.Price || 0,
              SaleItem_ID: item.ID,
              Remarks: ''
            });
            setSelectedItems(prev => ({ ...prev, [item.ID]: item }));
          }
          // Immediately return focus to the barcode input
          focusBarcodeInput();
          
        } else {
          addNotification({
            type: 'error',
            title: 'Item Not Found',
            message: `No item found with ID: ${nextBarcode}`
          });
        }
      } catch (error) {
        console.error('Error fetching item by barcode:', error);
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to fetch item by barcode'
        });
      } finally {
        // dequeue
        setBarcodeQueue(prev => prev.slice(1));
        setIsSearchingByBarcode(false);
        setIsProcessingBarcodeQueue(false);
      }
    };
    processNext();
  }, [barcodeQueue, isProcessingBarcodeQueue, prepend, move, addNotification, fields.length]);

  // Scoped to `details` so the reduce runs when quantities change rather than
  // on every render of the form. Matters once a large CSV has been imported.
  const watchedDetails = useWatch({ control, name: 'details' });

  // Helper function to focus barcode input
  const focusBarcodeInput = () => {
    const el = barcodeInputRef.current;
    if (!el) return;
    // Blur any currently focused element to prevent lingering focus (e.g., quantity)
    if (document.activeElement && document.activeElement !== el && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    // Immediate focus/select
    if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    if (typeof el.select === 'function') el.select();
    // Fallback after render cycle
    setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus({ preventScroll: true });
        if (typeof barcodeInputRef.current.select === 'function') {
          barcodeInputRef.current.select();
        }
      }
    }, 0);
    // rAF fallback in case layout shifts steal focus
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus({ preventScroll: true });
          if (typeof barcodeInputRef.current.select === 'function') {
            barcodeInputRef.current.select();
          }
        }
      });
    }
  };

  if (isLoadingPurchase) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderDetailRow = (index) => (
    <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2.5">

                </div>

                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item *
                    </label>
                    <div className="relative dropdown-container">
                      <div
                        onClick={() => !isEditing && toggleDropdown(index)}
                        onKeyDown={(e) => {
                          if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            toggleDropdown(index);
                          }
                        }}
                        tabIndex={isEditing ? -1 : 0}
                        role="button"
                        aria-expanded={dropdownStates[index]}
                        aria-haspopup="listbox"
                        className={`w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between transition-colors ${
                          isEditing
                            ? 'bg-gray-100 cursor-not-allowed'
                            : 'cursor-pointer bg-white hover:border-gray-400'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          {getSelectedItem(index) ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-semibold text-blue-600">
                                  {getSelectedItem(index).Name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {getSelectedItem(index).Name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatPrice(getSelectedItem(index).Price)}
                                  {getSelectedItem(index).Barcode && ` • Barcode: ${getSelectedItem(index).Barcode}`}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">Select an item</span>
                          )}
                        </div>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${dropdownStates[index] ? 'rotate-180' : ''}`} />
                      </div>

                      {!isEditing && dropdownStates[index] && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                          <div className="p-1.5 border-b border-gray-200">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search items..."
                                value={searchTerms[index] || ''}
                                onChange={(e) => setSearchTerm(index, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault(); // Prevent form submission
                                  } else if (e.key === 'Escape') {
                                    setDropdownStates(prev => ({ ...prev, [index]: false }));
                                  }
                                }}
                                className="w-full pl-10 pr-10 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                autoFocus
                              />
                              {isSearching[index] && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {isSearching[index] ? (
                              <div className="p-3 text-center">
                                <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                  <span>Searching items...</span>
                                </div>
                              </div>
                            ) : getFilteredItems(index).length > 0 ? (
                              getFilteredItems(index).map((item) => (
                                <div
                                  key={item.ID}
                                  onClick={() => selectItem(index, item)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      selectItem(index, item);
                                    }
                                  }}
                                  tabIndex={0}
                                  role="option"
                                  className="p-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 focus:bg-gray-50 focus:outline-none"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                      <span className="text-sm font-semibold text-gray-600">
                                        {item.Name?.charAt(0)?.toUpperCase() || '?'}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-900 truncate">
                                        {item.Name}
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">
                                        ID: {item.ID}
                                        {item.Barcode && ` • Barcode: ${item.Barcode}`}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-sm font-semibold text-blue-600">
                                        {formatPrice(item.Price)}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Stock: {item.WebsiteStock || 0}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="p-3 text-center text-gray-500 text-sm">
                                {searchTerms[index] ? 'No items found matching your search' : 'No items available'}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.details?.[index]?.SaleItem_ID && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.details[index].SaleItem_ID.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity *
                      </label>
                      <input
                        ref={(el) => {
                          quantityInputRefs.current[`quantity-${index + 1}`] = el;
                        }}
                        type="number"
                        step="1"
                        min="1"
                        placeholder="Enter quantity"
                        disabled={isEditing}
                        {...register(`details.${index}.Quantity`, {
                          required: 'Quantity is required',
                          min: { value: 1, message: 'Quantity must be at least 1' }
                        })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const currentQuantity = parseFloat(e.target.value) || 0;
                            if (currentQuantity > 0) {
                              focusBarcodeInput();
                            } else {
                              addNotification({
                                type: 'error',
                                title: 'Invalid Quantity',
                                message: 'Please enter a quantity greater than 0'
                              });
                              e.target.focus();
                              e.target.select();
                            }
                          }
                        }}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      {errors.details?.[index]?.Quantity && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.details[index].Quantity.message}
                        </p>
                      )}
                    </div>

                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Item price"
                        disabled={true}
                        readOnly={true}
                        {...register(`details.${index}.Price`, {
                          required: 'Price is required',
                          min: { value: 0, message: 'Price must be 0 or greater' }
                        })}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                      {errors.details?.[index]?.Price && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.details[index].Price.message}
                        </p>
                      )}
                    </div>

                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => removeDetail(index)}
                        className="p-1 mb-0.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/purchases')}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Purchase' : 'New Purchase'}
          </h1>
      
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit, (formErrors) => {
          const firstIndex = Number(Object.keys(formErrors.details || {})[0]);
          if (!Number.isNaN(firstIndex)) {
            rowVirtualizer.scrollToIndex(firstIndex, { align: 'center' });
          }
          const count = Object.keys(formErrors.details || {}).length;
          if (count > 0) {
            addNotification({
              type: 'error',
              title: 'Check the highlighted rows',
              message: `${count} row${count === 1 ? '' : 's'} need attention`,
            });
          }
        })}
        className="space-y-4"
      >
        <TotalsBar
          details={watchedDetails}
          importResult={importResult}
          onDismissImportResult={() => setImportResult(null)}
          onShowFailed={() => setIsImportOpen(true)}
        />

        {/* Purchase Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Purchase Information</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  {...register('PurchaseDate', { required: 'Purchase date is required' })}
                  disabled={isEditing}
                  className="w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              {errors.PurchaseDate && (
                <p className="text-red-600 text-sm mt-1">{errors.PurchaseDate.message}</p>
              )}
            </div>



            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Remarks
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <textarea
                  {...register('PurchaseRemarks')}
                  rows={1}
                  disabled={isEditing}
                  className="w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter purchase remarks..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Purchase Details */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Purchase Items</h2>
            </div>
            {!isEditing && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsImportOpen(true)}
                  className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  Import CSV
                </button>
                <button
                  type="button"
                  onClick={addDetail}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Item
                </button>
              </div>
            )}
          </div>

          {/* Barcode/Search Input - Only show in create mode */}
          {!isEditing && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search or Scan Item
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={barcodeInputRef}
                  type="text"
                  placeholder="Search item name or scan barcode..."
                  value={barcodeSearch}
                  onChange={(e) => setBarcodeSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleBarcodeSearch();
                    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                      if (barcodeSearchResults.length === 0) return;
                      e.preventDefault();
                      setShowBarcodeDropdown(true);
                      const step = e.key === 'ArrowDown' ? 1 : -1;
                      setBarcodeActiveIndex(prev =>
                        Math.min(Math.max(prev + step, 0), barcodeSearchResults.length - 1)
                      );
                    } else if (e.key === 'Escape') {
                      setShowBarcodeDropdown(false);
                    }
                  }}
                  onBlur={() => {
                    // Delay to allow click on dropdown item
                    setTimeout(() => setShowBarcodeDropdown(false), 200);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {(isSearchingByBarcode || isProcessingBarcodeQueue || isBarcodeSearching) && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
              {/* Search Results Dropdown */}
              {showBarcodeDropdown && barcodeSearchResults.length > 0 && (
                <div
                  className="relative z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  // Keep focus in the search box while a result is pressed. Otherwise
                  // the box blurs, the list closes 200ms later, and a click held any
                  // longer than that lands on nothing: the item is never added.
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {barcodeSearchResults.map((item, idx) => (
                    <div
                      key={item.ID}
                      ref={idx === barcodeActiveIndex ? barcodeActiveRowRef : null}
                      onClick={() => addItemFromSearch(item)}
                      // mousemove, not mouseenter: a list that opens under a parked
                      // cursor must not steal the highlight from the first result.
                      onMouseMove={() => setBarcodeActiveIndex(idx)}
                      className={`p-2.5 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${idx === barcodeActiveIndex ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-semibold text-gray-600">
                            {item.Name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {item.Name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            ID: {item.ID}
                            {item.Barcode && ` • Barcode: ${item.Barcode}`}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-blue-600">
                            {formatPrice(item.Price)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Stock: {item.Stock || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Barcode Queue Progress */}
              {(barcodeQueue.length > 0 || isProcessingBarcodeQueue) && (
                <div className="mt-1.5 text-xs text-gray-600 flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                  <span>Processing barcode{isProcessingBarcodeQueue ? '...' : ''}</span>
                  {barcodeQueue.length > 0 && (
                    <span className="px-2 py-0.5 bg-gray-100 rounded-full border text-gray-700">{barcodeQueue.length} pending</span>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1.5">
                Type to search items or scan barcode. Use ↑ ↓ to pick a result, Enter or click to add it.
              </p>
            </div>
          )}

          <div ref={rowsContainerRef}>
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: 'relative',
                width: '100%',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                <div
                  key={fields[virtualRow.index].id}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
                    paddingBottom: '0.75rem',
                    // Lift the row whose item dropdown is open above the
                    // following virtualized rows. Each virtual row is an
                    // absolutely-positioned sibling with its own transform
                    // (a separate stacking context), so the dropdown's inner
                    // z-50 can't escape it — the next row would otherwise
                    // paint over the open list.
                    zIndex: dropdownStates[virtualRow.index] ? 30 : undefined,
                  }}
                >
                  {renderDetailRow(virtualRow.index)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        {isEditing ? (
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => navigate('/purchases')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Back to Purchases
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500 max-w-xs">
              Draft saves the order without changing stock. Take it live later to apply the quantities.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/purchases')}
                className="px-3.5 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={() => { submitModeRef.current = 'draft'; }}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-1.5 border border-amber-400 text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
                title="Save without changing stock. You can take it live later."
              >
                <FileText className="h-4 w-4" />
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
              <button
                type="submit"
                onClick={() => { submitModeRef.current = 'live'; }}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? 'Saving...' : 'Create Purchase'}
              </button>
            </div>
          </div>
        )}
      </form>

      <ImportCsvModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onConfirm={handleImportConfirm}
      />
    </div>
  );
};

export default PurchaseForm;
